// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { flushSync } from 'react-dom';
import { Box, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import { FHButton, FHConfirmDialog, FProgressBar } from '../../../../base';
import { goalActions, treatmentPlanActions } from '../../../../../store';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { validateSchema } from '../../../../../helpers/validation-helpers';
import { goalSchema } from '../common/utils/validationSchema';
import GoalItem from './GoalItem';
import { areUserRolesIn } from '../../../../../helpers';
import { FORTA_GRAY4, FORTA_BLUE, FORTA_WHITE } from '../../../../../utils/colors';
import {
  validateInstanceMasteryCriteria,
  validateDurationMasteryCriteria,
  validateGoalAndGetErrors,
  validatePCGMasteryCriteria,
  validateRateMasteryCriteria,
  validateV2SAGMasteryCriteria,
} from '../common/utils/general';
import { willV2GoalBeActive } from './utils/general';
import {
  MAXIMUM_TARGET_INSTRUCTION_LENGTH,
  MINIMUM_REQUIRED_SENTENCE_LENGTH,
  Roles,
  Themes,
} from '../../../../../constants';
import { BAYS } from '../../../../../constants';
import { BehaviorDescriptionMap, TreatmentPlanPages } from '../../constants/general';
import {
  BaselineMeasurementVersion,
  DefaultInstanceMCValues,
  DefaultMeasurementUnitValueV2Goal,
  DefaultPercentageDCMValueV2Goal,
  GOAL_DETAILS_FIELD_IDS,
  GOAL_TARGET_NAME_MAX_CHARS,
  GOAL_TARGET_NAME_MIN_CHARS,
  GoalTypes,
  initialTreatmentPlanGoal,
  TPGoalDataCollectionMethod,
  TreatmentPlanResourceVersion,
} from '../common/constants/general';
import PlusIcon from '../../../../svgs/PlusIcon';

import type {
  IBehaviorGoalName,
  ICommonDurationMasteryCriteriaItem,
  ICommonRateMasteryCriteriaItem,
  IHypothesizedFunction,
  IInstanceMasteryCriteriaItem,
  IParencetageMasteryCriteriaItem,
  ISAGDurationMasteryCriteriaItem,
  ISAGInstanceMasteryCriteriaItem,
  ISAGMasteryCriteria,
  ISAGRateMasteryCriteriaItem,
  ITreatmentPlanGoal,
  ITreatmentPlanGoalTarget,
} from '../../../../../utils/types';
import type { RootState } from '../../../../../store';

interface GoalsContainerProps {
  type: GoalTypes;
  goalVersion: TreatmentPlanResourceVersion;
}

function getBaselineValue(goal: ITreatmentPlanGoal): number {
  return Number(goal.baseline_at_creation);
}

function getTotalAttempts(goal: ITreatmentPlanGoal): number | undefined {
  const measurementUnits = goal.measurement_units;
  return measurementUnits && 'total_attempts' in measurementUnits
    ? measurementUnits.total_attempts
    : undefined;
}

function isSAGInstanceMasteryCriteria(
  value: ITreatmentPlanGoal['mastery_criteria'],
): value is ISAGInstanceMasteryCriteriaItem {
  return (
    value !== null &&
    'acquisition' in value &&
    'maintenance_frequency' in value &&
    value.acquisition !== null &&
    'practice_duration_unit' in value.acquisition
  );
}

function isSAGPercentageMasteryCriteria(
  value: ITreatmentPlanGoal['mastery_criteria'],
): value is ISAGMasteryCriteria {
  return (
    value !== null &&
    'acquisition' in value &&
    'maintenance_frequency' in value &&
    value.acquisition !== null &&
    'total_attempts' in value.acquisition &&
    !('change_type' in value.acquisition)
  );
}

function isSAGRateMasteryCriteria(
  value: ITreatmentPlanGoal['mastery_criteria'],
): value is ISAGRateMasteryCriteriaItem {
  if (!value || !('acquisition' in value) || !('maintenance_frequency' in value)) {
    return false;
  }
  const { acquisition } = value;
  if (!acquisition || !('change_type' in acquisition)) {
    return false;
  }
  return !('measurement_mode' in acquisition) && !('practice_duration_unit' in acquisition);
}

function isSAGDurationMasteryCriteria(
  value: ITreatmentPlanGoal['mastery_criteria'],
): value is ISAGDurationMasteryCriteriaItem {
  return (
    value !== null &&
    'acquisition' in value &&
    'maintenance_frequency' in value &&
    value.acquisition !== null &&
    'measurement_mode' in value.acquisition
  );
}

function isInstanceMasteryCriteria(
  value: ITreatmentPlanGoal['mastery_criteria'],
): value is IInstanceMasteryCriteriaItem {
  return value !== null && 'practice_duration_unit' in value;
}

function isPercentageMasteryCriteria(
  value: ITreatmentPlanGoal['mastery_criteria'],
): value is IParencetageMasteryCriteriaItem {
  return value !== null && 'desired_amount' in value && !('acquisition' in value);
}

function isRateMasteryCriteria(
  value: ITreatmentPlanGoal['mastery_criteria'],
): value is ICommonRateMasteryCriteriaItem {
  return (
    value !== null &&
    'change_type' in value &&
    !('measurement_mode' in value) &&
    !('practice_duration_unit' in value) &&
    !('acquisition' in value)
  );
}

function isDurationMasteryCriteria(
  value: ITreatmentPlanGoal['mastery_criteria'],
): value is ICommonDurationMasteryCriteriaItem {
  return value !== null && 'measurement_mode' in value;
}

function GoalsContainer(props: GoalsContainerProps) {
  const { type = GoalTypes.SkillAcquisition, goalVersion } = props;

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    current: behavior,
    list: behaviors,
    goalNames,
  } = useSelector((state: RootState) => state.behaviors);
  const {
    current: treatmentPlan,
    list: treatmentPlans,
    loading: treatmentPlanLoading,
  } = useSelector((state: RootState) => state.treatmentPlans);
  const {
    loading: goalsLoading,
    behaviorReductionGoals,
    skillAcquisitionGoals,
    caregiverGoals,
    validationErrors,
    originalGoals,
  } = useSelector((state: RootState) => state.goals);
  const { multiTrialSummary } = useSelector((state: RootState) => state.sessions);

  const [submittings, setSubmittings] = useState<{ [key: number]: boolean } | null>(null);
  const [targetSaving, setTargetSaving] = useState<boolean>(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showDeleteGoalConfirmation, setShowDeleteGoalConfirmation] = useState<boolean>(false);
  const [goals, setGoals] = useState<ITreatmentPlanGoal[]>([]);
  const goalsContainerRef = useRef<HTMLDListElement | null>(null);
  const goalsRef = useRef(goals);
  const [hasNewGoal, setHasNewGoal] = useState<boolean>(false);
  const [goalToDelete, setGoalToDelete] = useState<number | null>(null);
  const [isValidationDataReady, setValidationDataReady] = useState(false);

  const tpBay = treatmentPlan?.bay ?? BAYS.BAY_2_6;

  useEffect(() => {
    if (!type) return;

    switch (type) {
      case GoalTypes.BehaviorReduction:
        setGoals(behaviorReductionGoals || []);
        break;
      case GoalTypes.SkillAcquisition:
        setGoals(skillAcquisitionGoals || []);
        break;
      case GoalTypes.Caregiver:
        setGoals(caregiverGoals || []);
        break;
      default:
        setGoals([]);
        break;
    }
  }, [type, behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals]);

  useEffect(() => {
    goalsRef.current = goals;

    setHasNewGoal(goals.some((goal: ITreatmentPlanGoal) => !Object.hasOwn(goal, 'id')));
    setShowScrollToTop(goals.some((goal) => goal.expanded));
  }, [goals]);

  useEffect(() => {
    if (!goalsLoading && !treatmentPlanLoading && treatmentPlan) {
      setValidationDataReady(true);
    } else {
      setValidationDataReady(false);
    }
  }, [goalsLoading, treatmentPlanLoading, treatmentPlan]);

  useEffect(() => {
    // Don't update badge until validation data is ready
    if (!isValidationDataReady) {
      return;
    }

    let errorCount = 0;

    if (validationErrors && Object.keys(validationErrors).length > 0) {
      Object.keys(validationErrors).forEach((goalType) => {
        Object.keys(validationErrors[goalType]).forEach((index) => {
          if (validationErrors[goalType][index]) {
            Object.keys(validationErrors[goalType][index]).forEach((key) => {
              if (key === 'targets') {
                const targetErrors = validationErrors[goalType][index][key];
                Object.keys(targetErrors).forEach((targetIndex) => {
                  Object.keys(targetErrors[targetIndex]).forEach((targetField) => {
                    if (targetErrors[targetIndex][targetField]) {
                      errorCount++;
                    }
                  });
                });
              } else {
                if (validationErrors[goalType][index][key]) {
                  errorCount++;
                }
              }
            });
          }
        });
      });

      const allGoals =
        tpBay === BAYS.BAY_2_5
          ? caregiverGoals
          : [
              ...(behaviorReductionGoals || []),
              ...(skillAcquisitionGoals || []),
              ...(caregiverGoals || []),
            ];

      //If there are goals with no targets, increment error count
      allGoals.forEach((goal) => {
        if (!goal.targets || goal.targets.length === 0) {
          errorCount++;
        }
      });
      if (tpBay !== BAYS.BAY_2_5) {
        // Active BRGs should be linked with at least 1 SAG goal
        behaviorReductionGoals.forEach((goal) => {
          const isActive = willV2GoalBeActive(goal);
          if (isActive && !goal?.linked_goals?.skill_acquisition?.length) {
            errorCount++;
          }
        });

        // Calculate num of inactive SAGs linked with GRGs
        behaviorReductionGoals.forEach((goal) => {
          if (goal?.linked_goals?.skill_acquisition?.length) {
            goal?.linked_goals?.skill_acquisition?.forEach((g) => {
              const sagGoal = skillAcquisitionGoals.find((sg) => sg.id === g.id);
              if (sagGoal) {
                const isSagNotActive = !willV2GoalBeActive(sagGoal);
                if (isSagNotActive) {
                  errorCount++;
                }
              }
            });
          }
        });

        // Totally 2 goals are required at least.
        // One goal in skill acquisition
        // One goal in Parent/caregiver
        if (skillAcquisitionGoals?.length === 0) {
          errorCount++;
        }
      }
      if (caregiverGoals?.length === 0) {
        errorCount++;
      }
    }
    dispatch(treatmentPlanActions.updateBadge({ [TreatmentPlanPages.Goals]: errorCount }));
  }, [
    isValidationDataReady,
    validationErrors,
    skillAcquisitionGoals,
    caregiverGoals,
    behaviorReductionGoals,
  ]);

  //Validate all types of goals on page load
  useEffect(() => {
    const allValidationErrors: any = {
      [GoalTypes.BehaviorReduction]: {},
      [GoalTypes.SkillAcquisition]: {},
      [GoalTypes.Caregiver]: {},
    };

    // Validate behavior reduction goals
    if (behaviorReductionGoals?.length) {
      behaviorReductionGoals.forEach((goal, index) => {
        const errors = validateGoalAndGetErrors(goal, goalVersion);
        allValidationErrors[GoalTypes.BehaviorReduction][index] = errors;
      });
    }

    // Validate skill acquisition goals
    if (skillAcquisitionGoals?.length) {
      skillAcquisitionGoals.forEach((goal, index) => {
        const errors = validateGoalAndGetErrors(goal, goalVersion);
        allValidationErrors[GoalTypes.SkillAcquisition][index] = errors;
      });
    }

    // Validate caregiver goals
    if (caregiverGoals?.length) {
      caregiverGoals.forEach((goal, index) => {
        const errors = validateGoalAndGetErrors(goal, goalVersion);
        allValidationErrors[GoalTypes.Caregiver][index] = errors;
      });
    }

    dispatch(
      goalActions.updateState({
        key: 'validationErrors',
        value: allValidationErrors,
      }),
    );
  }, [
    behaviorReductionGoals,
    skillAcquisitionGoals,
    caregiverGoals,
    goalVersion,
    treatmentPlan,
    treatmentPlans,
  ]);

  const validateGoal = (goal: ITreatmentPlanGoal, showAlerts = true) => {
    const showMissingMasteryCriteriaError = () => {
      if (showAlerts) {
        toast.error('Please update all fields in the Mastery Criteria section.');
      }
    };

    if (!goal) {
      if (showAlerts) {
        toast.error('Goal not found.');
      }
      return;
    }

    if (!goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]) {
      if (showAlerts) {
        toast.error('Focus area and short term goal fields are required.');
      }
      return;
    }

    if (!goal.date_introduced) {
      if (showAlerts) {
        toast.error('Baseline date is required.');
      }
      return;
    }

    if (goal.description?.includes('{') || goal.description?.includes('}')) {
      if (showAlerts) {
        toast.error("Goal description should not contain curly brackets '{' '}'");
      }
      return;
    }

    if (goal.baseline_at_creation === null) {
      if (showAlerts) {
        toast.error('Baseline level is required.');
      }
      return;
    }

    // Validate skill acquisition mastery criteria
    if (goal?.goal_type === GoalTypes.SkillAcquisition) {
      if (goal?.data_collection_method === TPGoalDataCollectionMethod.INSTANCES) {
        if (!isSAGInstanceMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const acquisitionErrors = validateInstanceMasteryCriteria(
          goal.mastery_criteria.acquisition,
          getBaselineValue(goal),
          TreatmentPlanResourceVersion.V2,
        );
        const generalizationErrors = validateInstanceMasteryCriteria(
          goal.mastery_criteria.generalization,
          getBaselineValue(goal),
          TreatmentPlanResourceVersion.V2,
        );
        const maintenanceErrors = validateInstanceMasteryCriteria(
          goal.mastery_criteria.maintenance,
          getBaselineValue(goal),
          TreatmentPlanResourceVersion.V2,
        );

        Object.keys(acquisitionErrors).forEach((key) => {
          if (acquisitionErrors[key]) {
            if (showAlerts) {
              toast.error(acquisitionErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(acquisitionErrors).length > 0) return;

        Object.keys(generalizationErrors).forEach((key) => {
          if (generalizationErrors[key]) {
            if (showAlerts) {
              toast.error(generalizationErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(generalizationErrors).length > 0) return;

        Object.keys(maintenanceErrors).forEach((key) => {
          if (maintenanceErrors[key]) {
            if (showAlerts) {
              toast.error(maintenanceErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(maintenanceErrors).length > 0) return;
      } else if (goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE) {
        if (!isSAGPercentageMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const acquisitionErrors = validateV2SAGMasteryCriteria(
          goal.mastery_criteria.acquisition,
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2
            ? (getTotalAttempts(goal) ?? null)
            : goal.mastery_criteria.acquisition.total_attempts,
          'acquisition',
        );
        const generalizationErrors = validateV2SAGMasteryCriteria(
          goal.mastery_criteria.generalization,
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2
            ? (getTotalAttempts(goal) ?? null)
            : goal.mastery_criteria.generalization.total_attempts,
          'generalization',
        );
        const maintenanceErrors = validateV2SAGMasteryCriteria(
          goal.mastery_criteria.maintenance,
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2
            ? (getTotalAttempts(goal) ?? null)
            : goal.mastery_criteria.maintenance.total_attempts,
          'maintenance',
        );

        Object.keys(acquisitionErrors).forEach((key) => {
          if (acquisitionErrors[key]) {
            if (showAlerts) {
              toast.error(acquisitionErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(acquisitionErrors).length > 0) return;

        Object.keys(generalizationErrors).forEach((key) => {
          if (generalizationErrors[key]) {
            if (showAlerts) {
              toast.error(generalizationErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(generalizationErrors).length > 0) return;

        Object.keys(maintenanceErrors).forEach((key) => {
          if (maintenanceErrors[key]) {
            if (showAlerts) {
              toast.error(maintenanceErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(maintenanceErrors).length > 0) return;
      } else if (goal?.data_collection_method === TPGoalDataCollectionMethod.RATE) {
        if (!isSAGRateMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const rateAcquisitionErrors = validateRateMasteryCriteria(
          goal.mastery_criteria.acquisition,
          getBaselineValue(goal),
        );

        const rateGeneralizationErrors = validateRateMasteryCriteria(
          goal.mastery_criteria.generalization,
          getBaselineValue(goal),
        );

        const rateMaintenanceErrors = validateRateMasteryCriteria(
          goal.mastery_criteria.maintenance,
          getBaselineValue(goal),
        );

        Object.keys(rateAcquisitionErrors).forEach((key) => {
          if (rateAcquisitionErrors[key]) {
            if (showAlerts) {
              toast.error(rateAcquisitionErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(rateAcquisitionErrors).length > 0) return;

        Object.keys(rateGeneralizationErrors).forEach((key) => {
          if (rateGeneralizationErrors[key]) {
            if (showAlerts) {
              toast.error(rateGeneralizationErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(rateGeneralizationErrors).length > 0) return;

        Object.keys(rateMaintenanceErrors).forEach((key) => {
          if (rateMaintenanceErrors[key]) {
            if (showAlerts) {
              toast.error(rateMaintenanceErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(rateMaintenanceErrors).length > 0) return;
      } else if (goal?.data_collection_method === TPGoalDataCollectionMethod.DURATION) {
        if (!isSAGDurationMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const durationAcquisitionErrors = validateDurationMasteryCriteria(
          goal.mastery_criteria.acquisition,
          getBaselineValue(goal),
        );

        const durationGeneralizationErrors = validateDurationMasteryCriteria(
          goal.mastery_criteria.generalization,
          getBaselineValue(goal),
        );

        const durationMaintenanceErrors = validateDurationMasteryCriteria(
          goal.mastery_criteria.maintenance,
          getBaselineValue(goal),
        );

        Object.keys(durationAcquisitionErrors).forEach((key) => {
          if (durationAcquisitionErrors[key]) {
            if (showAlerts) {
              toast.error(durationAcquisitionErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(durationAcquisitionErrors).length > 0) return;

        Object.keys(durationGeneralizationErrors).forEach((key) => {
          if (durationGeneralizationErrors[key]) {
            if (showAlerts) {
              toast.error(durationGeneralizationErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(durationGeneralizationErrors).length > 0) return;

        Object.keys(durationMaintenanceErrors).forEach((key) => {
          if (durationMaintenanceErrors[key]) {
            if (showAlerts) {
              toast.error(durationMaintenanceErrors[key]);
            }
            return;
          }
        });
        if (Object.keys(durationMaintenanceErrors).length > 0) return;
      }
      if (goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2) {
        const totalAttempts = getTotalAttempts(goal);
        if (
          totalAttempts &&
          +goal?.baseline_at_creation >= totalAttempts
        ) {
          if (showAlerts) {
            toast.error('Baseline should be less than total attempts.');
          }
          return;
        }
      }
    }

    // Validate caregiver mastery criteria
    if (goal?.goal_type === GoalTypes.Caregiver) {
      if (goal?.data_collection_method === TPGoalDataCollectionMethod.INSTANCES) {
        if (!isInstanceMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const errors = validateInstanceMasteryCriteria(
          goal.mastery_criteria,
          getBaselineValue(goal),
        );
        Object.keys(errors).forEach((key) => {
          if (errors[key]) {
            if (showAlerts) {
              toast.error(errors[key]);
            }
            return;
          }
        });
        if (Object.keys(errors).length > 0) return;
      } else if (goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE) {
        if (!isPercentageMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const errors = validatePCGMasteryCriteria(
          goal.mastery_criteria,
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2
            ? (getTotalAttempts(goal) ?? null)
            : goal.mastery_criteria.total_attempts,
          TreatmentPlanResourceVersion.V2,
          goal?.resource_versions?.baseline_measurement,
        );
        Object.keys(errors).forEach((key) => {
          if (errors[key]) {
            if (showAlerts) {
              toast.error(errors[key]);
            }
            return;
          }
        });
        if (Object.keys(errors).length > 0) return;
      } else if (goal?.data_collection_method === TPGoalDataCollectionMethod.RATE) {
        if (!isRateMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const errors = validateRateMasteryCriteria(
          goal.mastery_criteria,
          getBaselineValue(goal),
        );
        Object.keys(errors).forEach((key) => {
          if (errors[key]) {
            if (showAlerts) {
              toast.error(errors[key]);
            }
            return;
          }
        });
        if (Object.keys(errors).length > 0) return;
      } else if (goal?.data_collection_method === TPGoalDataCollectionMethod.DURATION) {
        if (!isDurationMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const errors = validateDurationMasteryCriteria(
          goal.mastery_criteria,
          getBaselineValue(goal),
        );
        Object.keys(errors).forEach((key) => {
          if (errors[key]) {
            if (showAlerts) {
              toast.error(errors[key]);
            }
            return;
          }
        });
        if (Object.keys(errors).length > 0) return;
      }

      if (goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2) {
        const totalAttempts = getTotalAttempts(goal);
        if (
          totalAttempts &&
          +goal?.baseline_at_creation >= totalAttempts
        ) {
          if (showAlerts) {
            toast.error('Baseline should be less than total attempts.');
          }
          return;
        }
      }
    }

    // Validate BRG mastery criteria
    if (goal?.goal_type === GoalTypes.BehaviorReduction) {
      if (goal?.data_collection_method === TPGoalDataCollectionMethod.INSTANCES) {
        if (!isInstanceMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const errors = validateInstanceMasteryCriteria(
          goal.mastery_criteria,
          getBaselineValue(goal),
        );
        Object.keys(errors).forEach((key) => {
          if (errors[key]) {
            if (showAlerts) {
              toast.error(errors[key]);
            }
            return;
          }
        });
        if (Object.keys(errors).length > 0) return;
      } else if (goal?.data_collection_method === TPGoalDataCollectionMethod.RATE) {
        if (!isRateMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const errors = validateRateMasteryCriteria(
          goal.mastery_criteria,
          getBaselineValue(goal),
        );
        Object.keys(errors).forEach((key) => {
          if (errors[key]) {
            if (showAlerts) {
              toast.error(errors[key]);
            }
            return;
          }
        });
        if (Object.keys(errors).length > 0) return;
      } else if (goal?.data_collection_method === TPGoalDataCollectionMethod.DURATION) {
        if (!isDurationMasteryCriteria(goal.mastery_criteria)) {
          showMissingMasteryCriteriaError();
          return;
        }
        const errors = validateDurationMasteryCriteria(
          goal.mastery_criteria,
          getBaselineValue(goal),
        );
        Object.keys(errors).forEach((key) => {
          if (errors[key]) {
            if (showAlerts) {
              toast.error(errors[key]);
            }
            return;
          }
        });
        if (Object.keys(errors).length > 0) return;
      }
    }

    if (goal.expected_mastery_date === null) {
      if (showAlerts) {
        toast.error('Expected mastery date is required.');
      }
      return;
    }

    if (!goal.description || goal.description?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
      if (showAlerts) {
        toast.error(
          `The goal description must be at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters long`,
        );
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      goal.hypothesized_functions?.length === 0
    ) {
      if (showAlerts) {
        toast.error('Hypothesized function field is required.');
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] &&
      !goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]
    ) {
      if (showAlerts) {
        toast.error(
          'Associated dsm5 symptom must be provided when goal is to correct maladaptive behavior',
        );
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.operational_definition ||
        goal.operational_definition?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(
          `Operational definition requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.objective || goal.objective?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(`Objective requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`);
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.antecedent_modifications ||
        goal.antecedent_modifications.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(
          `Antecedent modifications requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.consequence_modifications ||
        goal.consequence_modifications.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(
          `Consequence modifications requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (!goal.description || goal.description?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
      if (showAlerts) {
        toast.error(
          `Description requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (goal.targets.length === 0) {
      if (showAlerts) {
        toast.error('Goal should have at least one target.');
      }
      return;
    }

    if (goal.targets?.some((target) => target?.label?.length < GOAL_TARGET_NAME_MIN_CHARS)) {
      if (showAlerts) {
        toast.error(`Target name requires at least ${GOAL_TARGET_NAME_MIN_CHARS} characters.`);
      }
      return;
    }

    if (goal.targets?.some((target) => target?.label?.length > GOAL_TARGET_NAME_MAX_CHARS)) {
      if (showAlerts) {
        toast.error(`Target name requires maximum ${GOAL_TARGET_NAME_MAX_CHARS} characters.`);
      }
      return;
    }

    if (
      goal.targets?.some((target) => target?.instruction?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(
          `Target instruction requires minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (
      goal.targets?.some(
        (target) => target?.instruction?.length > MAXIMUM_TARGET_INSTRUCTION_LENGTH,
      )
    ) {
      if (showAlerts) {
        toast.error(
          `Target instruction has more than ${MAXIMUM_TARGET_INSTRUCTION_LENGTH} characters.`,
        );
      }
      return;
    }

    if (Object.hasOwn(goal, 'id') && goal.targets?.some((target) => target.is_editing)) {
      if (showAlerts) {
        toast.error(`You must save the editing targets before save goal.`);
      }
      return;
    }

    const goalNameIds = [
      ...behaviorReductionGoals,
      ...skillAcquisitionGoals,
      ...caregiverGoals,
    ].map((goal: ITreatmentPlanGoal) => goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]);
    const isGoalDuplicate = goalNameIds?.some((id, index) => goalNameIds?.indexOf(id) !== index);
    if (isGoalDuplicate) {
      if (showAlerts) {
        toast.error('Goal with same goal name already exists.');
      }
      return;
    }

    if (goal.targets?.length > 0) {
      const labels = goal.targets?.map((target) => target?.label);
      const isTargetDuplicate = labels?.some((label, index) => labels.indexOf(label) !== index);
      if (isTargetDuplicate) {
        if (showAlerts) {
          toast.error('Target duplicated.');
        }
        return;
      }
    }

    return true;
  };

  const validateAndSetErrors = useCallback(
    (data: ITreatmentPlanGoal, index: number, showAlerts = true) => {
      const errors = validateGoalAndGetErrors(data, goalVersion);
      dispatch(
        goalActions.updateState({
          key: 'validationErrors',
          value: {
            ...validationErrors,
            [type]: {
              ...validationErrors[type],
              [index]: errors,
            },
          },
        }),
      );
      const isValid = validateGoal(data, showAlerts);
      return isValid;
    },
    [dispatch, type, goalVersion, validationErrors, validateGoal],
  );

  const fetchSaveGoal = async (goal: ITreatmentPlanGoal) => {
    if (!goal || !treatmentPlan) return;

    const data = {
      ...goal,
      goal_type: type,
    };

    let response;
    if (Object.hasOwn(data, 'id')) {
      // update existing goal
      response = await dispatch(
        goalActions.updateGoal({
          treatmentPlanId: treatmentPlan.id,
          data,
          getAccessTokenSilently,
        }),
      );
    } else {
      // create new goal
      response = await dispatch(
        goalActions.createGoal({
          treatmentPlanId: treatmentPlan.id,
          data,
          getAccessTokenSilently,
        }),
      );
    }

    return response;
  };

  const fetchCreateTarget = async (goalId: number, target: ITreatmentPlanGoalTarget) => {
    if (!treatmentPlan || !goalId || !target) return;

    const res = await dispatch(
      goalActions.createGoalTarget({
        treatmentPlanId: treatmentPlan.id,
        goalId,
        data: target,
        getAccessTokenSilently,
      }),
    );

    if (!res || res.error) return;

    return res.payload;
  };

  const fetchUpdateTarget = async (goalId: number, target: ITreatmentPlanGoalTarget) => {
    if (!treatmentPlan || !goalId || !target) return;

    const res = await dispatch(
      goalActions.updateGoalTarget({
        treatmentPlanId: treatmentPlan.id,
        goalId,
        data: target,
        getAccessTokenSilently,
      }),
    );

    if (!res || res.error) return;

    return res.payload;
  };

  const fetchSaveTargets = async (goalId: number, targets: ITreatmentPlanGoalTarget[]) => {
    if (!goalId || !targets) return [];

    return await Promise.all(
      targets.map(async (target: ITreatmentPlanGoalTarget, index: number) => {
        let res;
        if (target.id) {
          res = await fetchUpdateTarget(goalId, target);
        } else {
          res = await fetchCreateTarget(goalId, { ...target, order: index });
        }
        return res;
      }),
    );
  };

  const handleAddNewGoal = () => {
    if (!behavior) {
      toast.error('Behavior is required to add new goal');
      return;
    }

    let newGoal = {
      ...initialTreatmentPlanGoal,
      goal_name: {
        behavior_id: '',
        label: '',
      },
      goal_name_id: '',
      goal_type: type,
    };

    if (type === GoalTypes.BehaviorReduction) {
      newGoal = {
        ...newGoal,
        operational_definition: '',
        objective: '',
        antecedent_modifications: '',
        consequence_modifications: '',
        description: '',
        hypothesized_functions: [],
        data_collection_method: TPGoalDataCollectionMethod.INSTANCES,
        linked_skill_acquisition_goal_ids: [],
        mastery_criteria: {
          ...DefaultInstanceMCValues(GoalTypes.BehaviorReduction, TreatmentPlanResourceVersion.V2),
        },
      };
    } else if (type === GoalTypes.SkillAcquisition) {
      newGoal = {
        ...newGoal,
        description: '',
        data_collection_method: TPGoalDataCollectionMethod.PERCENTAGE,
        mastery_criteria: {
          ...DefaultPercentageDCMValueV2Goal(GoalTypes.SkillAcquisition),
        },
        measurement_units: DefaultMeasurementUnitValueV2Goal,
      };
    } else {
      newGoal = {
        ...newGoal,
        description: '',
        data_collection_method: TPGoalDataCollectionMethod.PERCENTAGE,
        mastery_criteria: {
          ...DefaultPercentageDCMValueV2Goal(GoalTypes.Caregiver),
        },
        measurement_units: DefaultMeasurementUnitValueV2Goal,
      };
    }

    flushSync(() => {
      setGoals((prev) => [...prev, newGoal]);
    });
    goalsContainerRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth' });

    updateGoalsAndErrors([...goals, newGoal]);
  };

  const isTargetClientProgressUpdate = useCallback((index: number, key: string, value: any[]) => {
    if (key !== 'targets') return false;
    const goals = goalsRef.current;
    const oldTargets = goals[index].targets;
    return value.every((newTarget, i) => {
      const oldTarget = oldTargets[i];
      const diffKeys = Object.keys(newTarget).filter(
        (k) =>
          Object.entries(newTarget).find(([field]) => field === k)?.[1] !==
            Object.entries(oldTarget ?? {}).find(([field]) => field === k)?.[1] &&
          k !== 'client_progress_expanded' &&
          k !== 'dirty',
      );
      return diffKeys.length === 0;
    });
  }, []);

  const handleUpdateGoalField = useCallback(
    (index: number, key: string, value: any) => {
      const goals = goalsRef?.current;
      if (
        key !== 'expanded' &&
        key !== 'is_active' &&
        !isTargetClientProgressUpdate(index, key, value)
      ) {
        validateAndSetErrors({ ...goals[index], [key]: value }, index, false);
      }

      const dirty = isTargetClientProgressUpdate(index, key, value)
        ? goals[index]?.['dirty'] || false
        : key === 'is_active' || key === 'is_editing' || key === 'expanded' || key === 'targets'
          ? goals[index]?.['dirty'] || false
          : goals[index]?.dirty ||
            Object.entries(goals[index] ?? {}).find(([field]) => field === key)?.[1] !== value;

      if (key === GOAL_DETAILS_FIELD_IDS.GOAL_NAME) {
        let descriptionVal = '';
        if (value?.behavior?.id) {
          const behaviorLabel = behaviors.find((b) => b.id === value?.behavior?.id)?.label;
          descriptionVal =
            Object.entries(BehaviorDescriptionMap).find(([label]) => label === behaviorLabel)?.[1] ??
            '';
        }

        dispatch(
          goalActions.updateGoals({
            type,
            list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
              index === i
                ? {
                    ...goal,
                    [key]: value,
                    goal_name_id: '',
                    dirty,
                    description: descriptionVal ? descriptionVal : goal.description,
                  }
                : goal,
            ),
          }),
        );
      } else if (key === GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID) {
        dispatch(
          goalActions.updateGoals({
            type,
            list: goals.map((goal: ITreatmentPlanGoal, i: number) => {
              if (index !== i) return goal;
              const behaviorId = goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id;
              const resolvedGoalName =
                value != null && goalNames?.[behaviorId]
                  ? goalNames[behaviorId].find(
                      (goalName: IBehaviorGoalName) =>
                        goalName.id === value || goalName.id === Number(value),
                    )
                  : null;
              return {
                ...goal,
                [key]: value,
                goal_name:
                  value == null
                    ? goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]
                    : (resolvedGoalName ?? goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]),
                dirty,
              };
            }),
          }),
        );
      } else if (key === 'is_active') {
        dispatch(
          goalActions.updateGoals({
            type,
            list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
              index === i
                ? {
                    ...goal,
                    [key]: value.activeStatus,
                    reason: value.reason,
                    dirty,
                    targets: goal.targets.map((target) => ({
                      ...target,
                      is_active: value.activeStatus ? true : false,
                    })),
                  }
                : goal,
            ),
          }),
        );
      } else if (key === 'is_editing') {
        dispatch(
          goalActions.updateGoals({
            type,
            list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
              index === i
                ? {
                    ...goal,
                    [key]: value,
                    expanded: value ? true : goal.expanded,
                    dirty,
                  }
                : goal,
            ),
          }),
        );
      } else if (key === GOAL_DETAILS_FIELD_IDS.LINKED_SKILL_ACQUISITION_GOALS) {
        dispatch(
          goalActions.updateGoals({
            type,
            list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
              index === i
                ? {
                    ...goal,
                    linked_skill_acquisition_goal_ids:
                      value?.skill_acquisition?.map((goal: any) => goal.id) || [],
                    linked_goals: {
                      skill_acquisition: value?.skill_acquisition || [],
                      behavior_reduction: value?.behavior_reduction || [],
                    },
                    dirty,
                  }
                : goal,
            ),
          }),
        );
      } else {
        dispatch(
          goalActions.updateGoals({
            type,
            list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
              index === i ? { ...goal, [key]: value, dirty } : goal,
            ),
          }),
        );
      }
    },
    [type, dispatch, behaviors, goalNames, validateAndSetErrors, isTargetClientProgressUpdate],
  );

  const handleSaveGoal = async (index: number) => {
    const goals = goalsRef.current;
    const data = { ...goals[index] };
    const isValid = validateAndSetErrors(data, index, true);
    if (!isValid) {
      setSubmittings((prev) => ({ ...prev, [index]: false }));
      return;
    }

    const { payload } = await fetchSaveGoal(data);
    if (!payload) {
      setSubmittings((prev) => ({ ...prev, [index]: false }));
      return;
    }
    const goal = {
      ...payload,
      targets: payload.targets
          ? payload.targets.sort(
            (a: ITreatmentPlanGoalTarget, b: ITreatmentPlanGoalTarget) =>
              a.order - b.order,
          )
        : [],
    };

    let targets: ITreatmentPlanGoalTarget[] = [];
    let hypothesizedFunction: IHypothesizedFunction;
    if (!data.id && data.targets.length > 0) {
      const newTargets = data.targets.map((target) => ({
        ...target,
      }));
      targets = await fetchSaveTargets(goal?.id, newTargets);
    }

    if (!data.id && data.hypothesized_functions && data.hypothesized_functions.length > 0) {
      const response = await dispatch(
        goalActions.createHypothesizedFunctionRecord({
          getAccessTokenSilently,
          treatmentPlanId: treatmentPlan?.id,
          goalId: goal.id,
          data: { ...data.hypothesized_functions[0], date_selected: goal.date_introduced },
        }),
      );
      if (!response.error) {
        hypothesizedFunction = response.payload;
      }
    }

    dispatch(
      goalActions.updateGoals({
        type,
        list: goals.map((prev: ITreatmentPlanGoal, i: number) =>
          index === i
            ? targets.length > 0
              ? {
                  ...goal,
                  targets,
                  dirty: false,
                  expanded: true,
                  hypothesized_functions: data.hypothesized_functions?.[0]?.id
                    ? data.hypothesized_functions.map((hf, i) => {
                        if (i === 0) {
                          return { ...hf, date_selected: goal.date_introduced };
                        }
                        return hf;
                      })
                    : hypothesizedFunction
                      ? [hypothesizedFunction]
                      : [],
                }
              : {
                  ...goal,
                  dirty: false,
                  expanded: true,
                  hypothesized_functions: data.hypothesized_functions?.[0]?.id
                    ? data.hypothesized_functions.map((hf, i) => {
                        if (i === 0) {
                          return { ...hf, date_selected: goal.date_introduced };
                        }
                        return hf;
                      })
                    : hypothesizedFunction
                      ? [hypothesizedFunction]
                      : [],
                }
            : prev,
        ),
      }),
    );

    // Update linked goal info in behavior reduction goal
    if (type === GoalTypes.SkillAcquisition) {
      updateLinkedBehaviorReductionGoals(goal);
    } else if (type === GoalTypes.BehaviorReduction) {
      updateLinkedSkillAcquisitionGoals(goal);
    }

    setSubmittings((prev) => ({ ...prev, [index]: false }));
    toast.success('Goal saved.');
  };

  const updateLinkedSkillAcquisitionGoals = (goal: ITreatmentPlanGoal) => {
    // Get the linked SA goals array
    const linkedSkillAcquisitionGoals = goal.linked_goals?.skill_acquisition || [];

    // Update all SA goals
    dispatch(
      goalActions.updateGoals({
        type: GoalTypes.SkillAcquisition,
        list: skillAcquisitionGoals.map((sa: ITreatmentPlanGoal) => {
          // Check if this SA goal is in the linked goals list
          const isLinked = linkedSkillAcquisitionGoals.some((linkedSA) => linkedSA.id === sa.id);

          if (isLinked) {
            // If linked, update/add the BR goal reference
            return {
              ...sa,
              linked_goals: {
                ...sa.linked_goals,
                behavior_reduction: [
                  ...(sa.linked_goals?.behavior_reduction?.filter((br) => br.id !== goal.id) || []),
                  { id: goal.id, goal_name: goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME] },
                ],
              },
            };
          } else {
            // If not linked, remove the BR goal reference if it exists
            return {
              ...sa,
              linked_goals: {
                ...sa.linked_goals,
                behavior_reduction:
                  sa.linked_goals?.behavior_reduction?.filter((br) => br.id !== goal.id) || [],
              },
            };
          }
        }),
      }),
    );
  };

  const updateLinkedBehaviorReductionGoals = (goal: ITreatmentPlanGoal) => {
    // Update all SA linked_goals
    dispatch(
      goalActions.updateGoals({
        type: GoalTypes.BehaviorReduction,
        list: behaviorReductionGoals.map((br: ITreatmentPlanGoal) =>
          goal?.linked_goals?.behavior_reduction?.find((linkedBR) => linkedBR.id === br.id)
            ? {
                ...br,
                linked_goals: {
                  skill_acquisition:
                    br.linked_goals?.skill_acquisition?.map((sa) => {
                      if (sa.id === goal.id) {
                        return {
                          ...sa,
                          goal_name: goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME] ?? {},
                        };
                      }
                      return sa;
                    }) ?? [],
                },
              }
            : br,
        ),
      }),
    );
  };

  const handleDeleteGoal = async (index: number) => {
    setGoalToDelete(index);
    setShowDeleteGoalConfirmation(true);
  };

  const confirmDeleteGoal = async () => {
    setShowDeleteGoalConfirmation(false);
    if (goalToDelete === null) return;

    const index = goalToDelete;
    setSubmittings((prev) => ({ ...prev, [index]: true }));

    if (goals[index] != null && Object.hasOwn(goals[index], 'id')) {
      // call delete api
      const res = await dispatch(
        goalActions.removeGoal({
          treatmentPlanId: treatmentPlan?.id,
          goalId: goals[index].id,
          getAccessTokenSilently,
        }),
      );

      if (!res || res.error) {
        setSubmittings((prev) => ({ ...prev, [index]: false }));
        return;
      }
      await dispatch(
        goalActions.getGoals({
          treatmentPlanId: treatmentPlan?.id,
          include_pending:
            areUserRolesIn(user?.roles, [Roles.Vbt]) &&
            !areUserRolesIn(user?.roles, [Roles.AssistantAssessor])
              ? false
              : true,
          getAccessTokenSilently,
        }),
      );
    }

    const filteredList = goals.filter((goal: ITreatmentPlanGoal, i: number) => i !== index);
    updateGoalsAndErrors(filteredList);

    setSubmittings((prev) => ({ ...prev, [index]: false }));
    setShowDeleteGoalConfirmation(false);
    setGoalToDelete(null);
    toast.success('Goal deleted.');
  };

  const handleCancelGoalChange = (index: number) => {
    if (!originalGoals || !originalGoals[type]) {
      updateGoalsAndErrors(goals.filter((_, i) => index !== i));
      return;
    }

    const originalGoal = originalGoals[type]?.[index];

    const goalList =
      originalGoal && Object.hasOwn(originalGoal, 'id')
        ? goals.map((goal, i) => (index === i ? { ...originalGoal, is_editing: false } : goal))
        : goals.filter((_, i) => index !== i);
    updateGoalsAndErrors(goalList);
  };

  const updateGoalsAndErrors = (goals: ITreatmentPlanGoal[]) => {
    dispatch(
      goalActions.updateGoals({
        type,
        list: goals,
      }),
    );

    let errors: any = {};
    goals.forEach((goal, index) => {
      const { dirty: _dirty, is_editing: _is_editing, expanded: _expanded, ...rest } = goal;
      Object.keys(rest).forEach((key) => {
        const isValid = validateSchema(goalSchema, key, rest);
        errors = {
          ...errors,
          [index]: {
            ...errors[index],
            [key]: !isValid,
          },
        };
      });
    });

    dispatch(
      goalActions.updateState({
        key: 'validationErrors',
        value: {
          ...validationErrors,
          [type]: errors,
        },
      }),
    );
  };

  const handleSaveTarget = async (
    goalIndex: number,
    targetIndex: number,
    target: ITreatmentPlanGoalTarget,
  ) => {
    if (!goals[goalIndex]) return;

    if (!(goals[goalIndex] != null && Object.hasOwn(goals[goalIndex], 'id'))) {
      updateTargetByIndex(goalIndex, targetIndex, target);
      return;
    }

    if (target?.label?.length < 2) {
      toast.error('Target requires at least 2 characters.');
      updateTargetByIndex(goalIndex, targetIndex, target);
      return;
    }

    const goalId = goals[goalIndex].id;
    if (goalId == null) {
      updateTargetByIndex(goalIndex, targetIndex, target);
      return;
    }

    let savedTarget: ITreatmentPlanGoalTarget = target;
    setTargetSaving(true);
    if (Object.hasOwn(target, 'id')) {
      savedTarget = await fetchUpdateTarget(goalId, target);
    } else {
      const targetOrders = goals[goalIndex].targets
        .filter((target) => !!target.order || target.order === 0)
        .map((target) => target.order);
      const lastTargetOrder = targetOrders.length > 0 ? Math.max(...targetOrders) : 0;
      savedTarget = await fetchCreateTarget(goalId, {
        ...target,
        order: lastTargetOrder + 1,
      });
    }
    setTargetSaving(false);
    if (!savedTarget) {
      return;
    }

    updateTargetByIndex(goalIndex, targetIndex, savedTarget);
    toast.success('Target saved.');
  };

  const updateTargetByIndex = (
    goalIndex: number,
    targetIndex: number,
    target: ITreatmentPlanGoalTarget,
  ) => {
    const goalList = goals.map((prev: ITreatmentPlanGoal, i: number) =>
      goalIndex === i
        ? {
            ...prev,
            targets: prev.targets.map((t, ti) => (ti === targetIndex ? target : t)),
          }
        : prev,
    );
    dispatch(
      goalActions.updateGoals({
        type,
        list: goalList,
      }),
    );

    // Update target validation
    validateAndSetErrors(goalList[goalIndex], goalIndex, false);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          width: '100%',
          backgroundColor: FORTA_BLUE.light3,
          borderRadius: '16px',
          p: 3,
          gap: '8px',
        }}
      >
        {goalsLoading && <FProgressBar />}
        <Box ref={goalsContainerRef}>
          {goals.map((goal: ITreatmentPlanGoal, index: number) => (
            <GoalItem
              key={index}
              index={index}
              values={goal}
              goalVersion={goalVersion}
              trialSummary={multiTrialSummary.find((summary) => summary?.goal_id === goal.id)}
              submitting={submittings ? submittings[index] : false}
              targetSaving={targetSaving}
              errors={
                treatmentPlan && validationErrors && validationErrors[type]
                  ? validationErrors[type][index]
                  : null
              }
              onUpdateGoalField={handleUpdateGoalField}
              onSave={() => {
                setSubmittings((prev) => ({ ...prev, [index]: true }));

                // Wait until update input fields on blur when click save
                setTimeout(() => {
                  handleSaveGoal(index);
                });
              }}
              onDelete={() => handleDeleteGoal(index)}
              onSaveTarget={(targetIndex: number, target: ITreatmentPlanGoalTarget) =>
                handleSaveTarget(index, targetIndex, target)
              }
              onCancelGoalChange={() => handleCancelGoalChange(index)}
            />
          ))}
        </Box>
        <FHButton
          data-testid="addGoalButton"
          startIcon={<PlusIcon color={FORTA_WHITE} />}
          colorTheme={Themes.Primary}
          sx={{ width: 'fit-content', height: '48px', justifyContent: 'flex-start' }}
          disabled={!isEditable || hasNewGoal}
          onClick={handleAddNewGoal}
        >
          Add goal
        </FHButton>
      </Box>
      <FHConfirmDialog
        open={showDeleteGoalConfirmation}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
          },
        }}
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        dialogStyles={{
          width: '650px',
          maxWidth: '650px',
        }}
        title={'Delete goal'}
        contentRenderer={() => (
          <Box
            sx={{
              height: '150px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography lineHeight={1.5} variant="h4" mb={1}>
              Are you sure you want to delete this goal?
            </Typography>
            <Typography lineHeight={1.5} variant="h4">
              This action cannot be undone.
            </Typography>
          </Box>
        )}
        submittext="Delete"
        submitBtnTestId="confirmDeleteBtn"
        canceltext="Cancel"
        cancelBtnTestId="cancel"
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          setGoalToDelete(null);
          setShowDeleteGoalConfirmation(false);
        }}
        onSubmit={confirmDeleteGoal}
      />
      {showScrollToTop && (
        //if goals accordion is expanded, show scroll to top button
        <Typography
          sx={{
            mt: 4,
            color: FORTA_GRAY4,
            textAlign: 'center',
            fontSize: '16px',
            textDecoration: 'underline',
            fontWeight: 500,
            cursor: 'pointer',
          }}
          onClick={() => {
            goalsContainerRef.current?.firstElementChild?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Scroll to top
        </Typography>
      )}
    </Box>
  );
}

export default GoalsContainer;
